import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import Campaign, { ICampaignAPI } from '@/common/models/campaign/Campaign';
import { DASH, toServerDate, toServerDateTime } from '@/globals';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

import MobileLineItemCreative, { IMobileLineItemCreativeAPI } from './MobileLineItemCreative';
import MobileLineItemLineItem, { IMobileLineItemLineItemAPI } from './MobileLineItemLineItem';
import MobileLineItemOptions from './MobileLineItemOptions';

export interface IMobileLineItemAPI {
  mobilelineitem_id: number;
  name: string;
  state: string;
  notes: string;
  campaign_id: number;
  start_date: string;
  end_date: string;
  budget: number;
  max_bid_cpm: number;
  freq_cap_count: number;
  freq_cap_period: string;
  format: string;
  goal: string;
  use_lineitem_geo_targeting: boolean;
  campaign: ICampaignAPI;
  mobilelineitem_creatives: IMobileLineItemCreativeAPI[];
  mobilelineitem_lineitems: IMobileLineItemLineItemAPI[];
  created_on_utc: string;
  modified_on_utc: string;
}

export default class MobileLineItem extends Validatable {
  name: string;
  state: string;
  notes: string;
  campaignID: number;
  startDate: DateTime;
  endDate: DateTime;
  budget: number;
  maxBidCPM: number;
  freqCapCount: number;
  freqCapPeriod: string;
  format: string;
  goal: string;
  useLineItemGeoTargeting: boolean;
  campaign: Campaign;
  mobileLineItemCreatives: MobileLineItemCreative[];
  mobileLineItemLineItems: MobileLineItemLineItem[];
  createdOnUtc: DateTime;
  modifiedOnUtc: DateTime;
  timestamp: DateTime;

  constructor(
    id = -1,
    name = '',
    state = MobileLineItemOptions.DRAFT,
    notes = null,
    campaignID = null,
    startDate = DateTime.local(),
    endDate = DateTime.local(),
    budget = 0,
    maxBidCPM = 12,
    freqCapCount = null,
    freqCapPeriod = null,
    format = null,
    goal = null,
    useLineItemGeoTargeting = true,
    campaign = null,
    mobileLineItemCreatives = [],
    mobileLineItemLineItems = [],
    createdOnUtc = null,
    modifiedOnUtc = null,
  ) {
    super();
    this.id = id;
    this.name = name;
    this.state = state;
    this.notes = notes;
    this.campaignID = campaignID;
    this.startDate = startDate;
    this.endDate = endDate;
    this.budget = budget;
    this.maxBidCPM = maxBidCPM;
    this.freqCapCount = freqCapCount;
    this.freqCapPeriod = freqCapPeriod;
    this.format = format;
    this.goal = goal;

    this.useLineItemGeoTargeting = useLineItemGeoTargeting;
    this.campaign = campaign;
    this.mobileLineItemCreatives = mobileLineItemCreatives;
    this.mobileLineItemLineItems = mobileLineItemLineItems;

    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.timestamp = DateTime.local();
  }

  get status() {
    if (this.state === 'D') return 'draft';
    if (this.state === 'S') return 'submitted';
    return null;
  }

  get lineItemIDs() {
    return (this.mobileLineItemLineItems || []).map((x) => x.lineItemID);
  }

  set lineItemIDs(ids) {
    const idsToAdd = ids.filter((id) => !this.lineItemIDs.includes(id));
    this.mobileLineItemLineItems = this.mobileLineItemLineItems.filter((mlili) => ids.includes(mlili.lineItemID));
    idsToAdd.forEach((id) => this.mobileLineItemLineItems.push(new MobileLineItemLineItem(-1, id, this.id)));
  }

  get creativeIDs() {
    return (this.mobileLineItemCreatives || []).map((x) => x.creativeID);
  }

  set creativeIDs(ids) {
    const idsToAdd = ids.filter((id) => !this.creativeIDs.includes(id));
    this.mobileLineItemCreatives = this.mobileLineItemCreatives.filter((mlic) => ids.includes(mlic.creativeID));
    idsToAdd.forEach((id) => this.mobileLineItemCreatives.push(new MobileLineItemCreative(-1, id, this.id)));
  }

  get endpoint() {
    return `/mobilelineitems/${this.id}`;
  }

  get startDateFormatted() {
    return i18n.global.d(this.startDate.toJSDate(), 'shortDate');
  }

  get endDateFormatted() {
    return i18n.global.d(this.endDate.toJSDate(), 'shortDate');
  }

  get budgetFormatted() {
    return i18n.global.n(this.budget, 'currency');
  }

  get formatFormatted() {
    let text = DASH;
    switch (this.format) {
      case MobileLineItemOptions.VIDEO:
        text = capitalize(i18n.global.t('names.video', { n: 1 }));
        break;
      case MobileLineItemOptions.IMAGE:
        text = capitalize(i18n.global.t('names.image', { n: 1 }));
        break;
      default:
        break;
    }
    return text;
  }

  get goalFormatted() {
    let text = DASH;
    switch (this.goal) {
      case MobileLineItemOptions.AWARENESS:
        text = capitalize(i18n.global.t('options.goals.raiseAwarenessOfMyBrandOrProduct', { n: 1 }));
        break;
      case MobileLineItemOptions.ONLINE:
        text = capitalize(i18n.global.t('options.goals.driveOnlineActionOrVisits', { n: 1 }));
        break;
      case MobileLineItemOptions.OFFLINE:
        text = capitalize(i18n.global.t('options.goals.driveOfflineOrInStoreSales', { n: 1 }));
        break;
      case MobileLineItemOptions.APP:
        text = capitalize(i18n.global.t('options.goals.driveAppInstallsOrEngagements', { n: 1 }));
        break;
      case MobileLineItemOptions.OTHER:
        text = capitalize(i18n.global.t('options.goals.other', { n: 1 }));
        break;
      default:
        break;
    }
    return text;
  }

  get frequencyCapFormatted() {
    let text = DASH;
    switch (this.freqCapPeriod) {
      case MobileLineItemOptions.LIFETIME:
        text = capitalize(
          i18n.global.t('options.frequencyCap.lifetime', this.freqCapCount, { count: this.freqCapCount }),
        );
        break;
      case MobileLineItemOptions.MONTH:
        text = capitalize(i18n.global.t('options.frequencyCap.month', this.freqCapCount, { count: this.freqCapCount }));
        break;
      case MobileLineItemOptions.WEEK:
        text = capitalize(i18n.global.t('options.frequencyCap.week', this.freqCapCount, { count: this.freqCapCount }));
        break;
      case MobileLineItemOptions.DAY:
        text = capitalize(i18n.global.t('options.frequencyCap.day', this.freqCapCount, { count: this.freqCapCount }));
        break;
      case MobileLineItemOptions.HOUR:
        text = capitalize(i18n.global.t('options.frequencyCap.hour', this.freqCapCount, { count: this.freqCapCount }));
        break;
      default:
        break;
    }

    return text;
  }

  get maxBidCPMFormatted() {
    if (this.maxBidCPM == null) return DASH;
    return i18n.global.n(this.maxBidCPM, 'currency');
  }

  get modifiedOnFormatted() {
    return i18n.global.d(this.modifiedOnUtc.toJSDate(), 'fullDateTime');
  }

  get isDeletable() {
    return this.isDraft;
  }

  get isDraft() {
    return this.state === MobileLineItemOptions.DRAFT;
  }

  static fromObject(obj) {
    return super.fromObject(obj, new MobileLineItem());
  }

  static fromApi(apiObject: IMobileLineItemAPI) {
    return MobileLineItem.fromObject({
      id: apiObject.mobilelineitem_id,
      name: apiObject.name,
      state: apiObject.state,
      notes: apiObject.notes,
      campaignID: apiObject.campaign_id,
      startDate: apiObject.start_date ? DateTime.fromISO(apiObject.start_date) : null,
      endDate: apiObject.end_date ? DateTime.fromISO(apiObject.end_date) : null,
      budget: apiObject.budget,
      useLineItemGeoTargeting: apiObject.use_lineitem_geo_targeting,
      maxBidCPM: apiObject.max_bid_cpm,
      freqCapCount: apiObject.freq_cap_count,
      freqCapPeriod: apiObject.freq_cap_period,
      format: apiObject.format,
      campaign: apiObject.campaign ? Campaign.fromApi(apiObject.campaign) : null,
      mobileLineItemCreatives: apiObject.mobilelineitem_creatives
        ? apiObject.mobilelineitem_creatives.map((x) => MobileLineItemCreative.fromApi(x))
        : null,
      mobileLineItemLineItems: apiObject.mobilelineitem_lineitems
        ? apiObject.mobilelineitem_lineitems.map((x) => MobileLineItemLineItem.fromApi(x))
        : null,
      createdOnUtc: apiObject.created_on_utc ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      modifiedOnUtc: apiObject.modified_on_utc ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      goal: apiObject.goal ? apiObject.goal : null,
    });
  }

  toApi(): IMobileLineItemAPI {
    const apiModel = {
      mobilelineitem_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      state: this.state,
      notes: this.notes,
      campaign_id: this.campaignID,
      start_date: toServerDate(this.startDate),
      end_date: toServerDate(this.endDate),
      budget: this.budget,
      use_lineitem_geo_targeting: this.useLineItemGeoTargeting,
      max_bid_cpm: this.maxBidCPM,
      freq_cap_count: this.freqCapCount,
      freq_cap_period: this.freqCapPeriod,
      format: this.format,
      goal: this.goal,
      campaign: this.campaign ? this.campaign.toApi() : undefined,
      mobilelineitem_creatives: this.mobileLineItemCreatives
        ? this.mobileLineItemCreatives.map((x) => x.toApi())
        : undefined,
      mobilelineitem_lineitems: this.mobileLineItemLineItems
        ? this.mobileLineItemLineItems.map((x) => x.toApi())
        : undefined,
      created_on_utc: this.createdOnUtc ? toServerDateTime(this.createdOnUtc) : undefined,
      modified_on_utc: this.modifiedOnUtc ? toServerDateTime(this.modifiedOnUtc) : undefined,
    };
    return apiModel;
  }

  static validatorCreateRules() {
    return {
      name: 'required|max:100|not_empty_or_whitespace',
      campaignID: 'required',
      budget: 'required|greater_than:0',
      startDate: 'required|before_or_equal:endDate',
      endDate: 'after_or_equal:startDate|required',
      format: 'required',
      goal: 'required',
    };
  }
}
